import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  ArrowLeft,
  Edit,
  Save,
  MapPin,
  Calendar,
  DollarSign,
  Users,
  Briefcase,
  Clock,
  CheckCircle,
  Star,
  Building,
  FileText,
  Loader,
  AlertTriangle,
  Share2,
  Globe,
  Copy,
  Check,
  Pause,
  X
} from 'lucide-react'
import { apiClient, handleApiError } from '@/lib/api'
import { apiRequest } from '@/config/api'
import AssociateCandidateModal from '@/components/positions/AssociateCandidateModal'
import CandidatesTableView from '@/components/candidates/CandidatesTableView'
import CollapsiblePositionDetails from '@/components/positions/CollapsiblePositionDetails'
import JobApplicantsView from '@/components/positions/JobApplicantsView'
import PositionInterviewsView from '@/components/positions/PositionInterviewsView'

interface PositionSkill {
  skill_id: number
  skill_name: string
  category?: string
  proficiency_level?: 'beginner' | 'intermediate' | 'advanced' | 'expert' | string
  is_good_to_have?: boolean
}

interface PositionCertification {
  certification_name: string
  certification_body?: string
  is_required: boolean
  expiry_required?: boolean
}

interface Position {
  id: string
  title: string
  description: string
  department: string
  location: string
  type: string
  status: string
  is_public: boolean
  public_url: string | null
  team_name?: string
  access_reason?: string
  skills_required?: PositionSkill[]
  certifications?: PositionCertification[]
  salary: {
    min: number
    max: number
    currency: string
  }
  candidates: {
    total: number
    new: number
    interviewed: number
    shortlisted: number
    hired: number
  }
  datePosted: string
  deadline: string
  priority: string
  requirements: string[]
  recruiter: string
  createdAt: string
  creatorEmail?: string
}

export default function PositionDetailPage({ params }: { params: { id: string } }) {
  const navigate = useNavigate()
  const location = useLocation()
  const [position, setPosition] = useState<Position | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isEditing, setIsEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [copied, setCopied] = useState(false)
  const [showAssociateModal, setShowAssociateModal] = useState(false)
  const [positionCandidates, setPositionCandidates] = useState<any[]>([])
  const [loadingCandidates, setLoadingCandidates] = useState(false)
  const [positionInterviews, setPositionInterviews] = useState<any[]>([])
  const [loadingInterviews, setLoadingInterviews] = useState(false)
  const [activeTab, setActiveTab] = useState<'candidates' | 'applicants' | 'interviews' | 'onhold' | 'rejected' | 'shares'>('candidates')
  const [applicantCounts, setApplicantCounts] = useState({
    job_applicants_count: 0,
    shortlisted_count: 0,
    onhold_count: 0,
    rejected_count: 0,
    interviews_count: 0
  })
  const [loadingCounts, setLoadingCounts] = useState(false)
  const [positionShares, setPositionShares] = useState<any[]>([])
  const [loadingShares, setLoadingShares] = useState(false)
  const [isReviewMode, setIsReviewMode] = useState(false)
  const [reviewActionLoading, setReviewActionLoading] = useState(false)
  const [showRejectModal, setShowRejectModal] = useState(false)
  const [rejectionReason, setRejectionReason] = useState('')
  const [currentUserId, setCurrentUserId] = useState<string | null>(null)

  const normalizeRequirements = (value: any): string[] => {
    if (!value) return []
    if (Array.isArray(value)) {
      return value
        .map(v => (typeof v === 'string' ? v : String(v)))
        .map(v => v.trim())
        .filter(Boolean)
    }
    if (typeof value === 'string') {
      const trimmed = value.trim()
      try {
        const parsed = JSON.parse(trimmed)
        if (Array.isArray(parsed)) {
          return parsed
            .map(v => (typeof v === 'string' ? v : String(v)))
            .map(v => v.trim())
            .filter(Boolean)
        }
      } catch (_) {
        // fallthrough to delimiter parsing
      }
      return trimmed
        .split(/[\n,;]+/)
        .map(item => item.replace(/^[-•\s]+/, '').trim())
        .filter(Boolean)
    }
    return [String(value)].filter(Boolean)
  }

  const normalizeSkills = (value: any): PositionSkill[] => {
    if (!value) return []

    const normalizeSkillObject = (item: any, index: number): PositionSkill | null => {
      if (!item) return null
      if (typeof item === 'string') {
        const name = item.trim()
        if (!name) return null
        return {
          skill_id: index,
          skill_name: name,
          category: 'General',
          proficiency_level: 'intermediate',
          is_good_to_have: false
        }
      }
      if (typeof item === 'object') {
        const skillName = item.skill_name || item.name || item.skill || ''
        if (!skillName) return null
        return {
          skill_id: item.skill_id || item.id || index,
          skill_name: skillName,
          category: item.category || item.type || 'General',
          proficiency_level: (item.proficiency_level || item.level || 'intermediate') as PositionSkill['proficiency_level'],
          is_good_to_have: Boolean(item.is_good_to_have || item.optional || item.nice_to_have)
        }
      }
      return null
    }

    if (Array.isArray(value)) {
      return value
        .map((item, idx) => normalizeSkillObject(item, idx))
        .filter((skill): skill is PositionSkill => Boolean(skill))
    }

    if (typeof value === 'string') {
      const trimmed = value.trim()
      try {
        const parsed = JSON.parse(trimmed)
        if (Array.isArray(parsed)) {
          return parsed
            .map((item, idx) => normalizeSkillObject(item, idx))
            .filter((skill): skill is PositionSkill => Boolean(skill))
        }
      } catch (_) {
        // fall through to delimiter parsing
      }
      return trimmed
        .split(/[\n,;]+/)
        .map(item => item.replace(/^[-•\s]+/, '').trim())
        .filter(Boolean)
        .map((name, idx) => normalizeSkillObject(name, idx))
        .filter((skill): skill is PositionSkill => Boolean(skill))
    }

    return []
  }

  useEffect(() => {
    loadPositionDetail()
    loadPositionCandidates()
    loadPositionInterviews()
    loadApplicantCounts()
    loadPositionShares()
  }, [params.id])

  useEffect(() => {
    const paramsSearch = new URLSearchParams(location.search)
    setIsReviewMode(paramsSearch.get('review') === '1')
  }, [location.search])

  useEffect(() => {
    try {
      const storedUser = localStorage.getItem('currentUser')
      if (storedUser) {
        const parsed = JSON.parse(storedUser)
        if (parsed?.id) {
          setCurrentUserId(parsed.id)
        }
      }
    } catch (e) {
      console.error('Failed to parse currentUser from storage', e)
    }
  }, [])

  // Listen for schedule modal events and enrich them with position data
  useEffect(() => {
    const handleScheduleModalWithPosition = (event: any) => {
      // Stop the original event
      event.stopPropagation()
      
      // Dispatch a new event with position data included
      window.dispatchEvent(new CustomEvent('openScheduleModal', {
        detail: {
          ...event.detail,
          positionData: position ? {
            id: position.id,
            title: position.title,
            department: position.department
          } : null
        }
      }))
    }

    window.addEventListener('openScheduleModal', handleScheduleModalWithPosition, true)
    
    return () => {
      window.removeEventListener('openScheduleModal', handleScheduleModalWithPosition, true)
    }
  }, [position])

  useEffect(() => {
    const handleInterviewScheduled = () => {
      loadPositionInterviews()
      loadApplicantCounts()
    }

    window.addEventListener('interviewScheduled', handleInterviewScheduled)
    return () => {
      window.removeEventListener('interviewScheduled', handleInterviewScheduled)
    }
  }, [params.id])

  const loadPositionDetail = async () => {
    try {
      setLoading(true)
      setError(null)
      
      const response = await apiClient.getPosition(params.id)
      
      if (response.success && response.data) {
        const positionData = (response.data as any).position || response.data
        const normalizedRequirements = normalizeRequirements(positionData.requirements)
        const normalizedSkills = normalizeSkills(positionData.skills_required || positionData.required_skills)
        setPosition({
          id: positionData.id?.toString() || params.id,
          title: positionData.title || '',
          description: positionData.description || '',
          department: positionData.department || '',
          location: positionData.location || positionData.primary_location || '',
          type: positionData.employment_type || 'full-time',
          status: positionData.status || 'active',
          is_public: positionData.is_public || false,
          public_url: positionData.public_url || null,
          team_name: positionData.team_name,
          access_reason: positionData.access_reason || positionData.team_name,
          salary: {
            min: positionData.salary_min || 0,
            max: positionData.salary_max || 0,
            currency: positionData.salary_currency || 'USD'
          },
          candidates: positionData.candidates || {
            total: 0,
            new: 0,
            interviewed: 0,
            shortlisted: 0,
            hired: 0
          },
          datePosted: positionData.datePosted || positionData.created_at || new Date().toISOString(),
          deadline: positionData.deadline || positionData.application_deadline || '',
          priority: positionData.priority || 'medium',
          requirements: normalizedRequirements,
          skills_required: normalizedSkills,
          certifications: positionData.certifications || [],
          recruiter: positionData.recruiter || 'System',
          createdAt: positionData.created_at || new Date().toISOString(),
          creatorEmail: positionData.user_email || positionData.created_by_email || positionData.hiring_manager_email
        })
      } else {
        setError('Position not found')
      }
    } catch (err) {
      setError('Failed to load position details')
      console.error('Failed to load position:', err)
    } finally {
      setLoading(false)
    }
  }

  const sendRejectionEmail = async (reason: string) => {
    if (!position?.creatorEmail) return

    try {
      await apiRequest('email/send', 'POST', {
        to_email: position.creatorEmail,
        subject: `Position ${position.title} was rejected`,
        content: ``,
        template_name: 'application_status_update',
        template_variables: {
          application_status: 'rejected',
          job_title: position.title,
          company_name: 'HireHubble',
          candidate_name: position.creatorEmail,
          status_message: reason || 'The position was rejected.',
          next_steps: 'Please review the feedback and resubmit the position for approval.'
        },
        is_html: true
      })
    } catch (err) {
      console.error('Failed to send rejection email', err)
    }
  }

  const handleApprove = async () => {
    if (!position) return
    try {
      setReviewActionLoading(true)
      await apiClient.updatePosition(params.id, { status: 'open' })
      setPosition({ ...position, status: 'open' })
      alert('Position approved and set to open.')
    } catch (err) {
      alert('Failed to approve position: ' + handleApiError(err))
    } finally {
      setReviewActionLoading(false)
    }
  }

  const handleReject = async () => {
    if (!position) return
    if (!rejectionReason.trim()) {
      alert('Please provide a rejection reason.')
      return
    }

    try {
      setReviewActionLoading(true)
      await apiClient.updatePosition(params.id, { status: 'rejected' })
      setPosition({ ...position, status: 'rejected' })
      await sendRejectionEmail(rejectionReason.trim())
      setShowRejectModal(false)
      alert('Position rejected and creator notified.')
    } catch (err) {
      alert('Failed to reject position: ' + handleApiError(err))
    } finally {
      setReviewActionLoading(false)
      setRejectionReason('')
    }
  }

  const loadPositionCandidates = async () => {
    try {
      setLoadingCandidates(true)
      
      console.log('🔍 DEBUG: Loading position candidates for:', params.id)
      const response = await apiClient.getPositionCandidates(params.id)
      console.log('🔍 DEBUG: Position candidates response:', response)
      
      if (response.success && response.data) {
        const candidates = response.data.candidates || []
        console.log('🔍 DEBUG: Setting candidates:', candidates.length, 'items')
        
        // Map API response to component expected format
        const mappedCandidates = candidates.map((candidate: any) => ({
          ...candidate,
          candidate_uuid: candidate.uuid,
          candidate_position_uuid: candidate.candidate_position_uuid || candidate.position_uuid || candidate.uuid, // Fallback to candidate uuid if needed
          full_name: candidate.name || `${candidate.first_name} ${candidate.last_name}`.trim()
        }))
        
        setPositionCandidates(mappedCandidates)
      } else {
        console.log('❌ DEBUG: API call failed or no data:', response)
        setPositionCandidates([])
      }
    } catch (err) {
      console.error('❌ Failed to load position candidates:', err)
      setPositionCandidates([])
    } finally {
      setLoadingCandidates(false)
    }
  }

  const loadPositionInterviews = async () => {
    try {
      setLoadingInterviews(true)
      const response = await apiClient.getPositionInterviews(params.id, { limit: 500 })

      if (response.success && response.data) {
        const payload = Array.isArray(response.data)
          ? response.data
          : (response.data as any).interviews || []
        setPositionInterviews(payload)
      } else {
        setPositionInterviews([])
      }
    } catch (err) {
      console.error('Failed to load position interviews:', err)
      setPositionInterviews([])
    } finally {
      setLoadingInterviews(false)
    }
  }

  const loadPositionShares = async () => {
    try {
      setLoadingShares(true)
      const response = await apiClient.getPositionShares(params.id)
      if (response.success && response.data) {
        const shares = (response.data as any).shares || response.data || []
        setPositionShares(shares)
      } else {
        setPositionShares([])
      }
    } catch (err) {
      console.error('Failed to load position shares:', err)
      setPositionShares([])
    } finally {
      setLoadingShares(false)
    }
  }


  const loadApplicantCounts = async () => {
    try {
      setLoadingCounts(true)
      
      // Use apiRequest from the api config instead of apiClient since it's not in apiClient yet
      const response = await apiRequest(`applicants/position/${params.id}/counts`)
      
      const data = await response.json()
      
      if (response.ok && data.success) {
        const counts = data.data || {}
        setApplicantCounts({
          job_applicants_count: counts.job_applicants_count || 0,
          shortlisted_count: counts.shortlisted_count || 0,
          onhold_count: counts.onhold_count || 0,
          rejected_count: counts.rejected_count || 0,
          interviews_count: counts.interviews_count || 0
        })
      }
    } catch (err) {
      console.error('Failed to load applicant counts:', err)
    } finally {
      setLoadingCounts(false)
    }
  }

  const handleInputChange = (field: keyof Position, value: any) => {
    setPosition(prev => prev ? { ...prev, [field]: value } : null)
  }

  const handleSalaryChange = (field: 'min' | 'max' | 'currency', value: any) => {
    setPosition(prev => prev ? {
      ...prev,
      salary: { ...prev.salary, [field]: value }
    } : null)
  }

  const handleSkillsChange = (skills: PositionSkill[]) => {
    setPosition(prev => prev ? { ...prev, skills_required: skills } : null)
  }

  const handleCertificationsChange = (certifications: PositionCertification[]) => {
    setPosition(prev => prev ? { ...prev, certifications } : null)
  }


  const copyPublicUrl = async () => {
    if (!position || !position.public_url) return
    
    try {
      await navigator.clipboard.writeText(position.public_url)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy URL:', err)
    }
  }

  const handleSave = async () => {
    if (!position) return

    setSaving(true)
    try {
      const updateData = {
        title: position.title,
        description: position.description,
        department: position.department,
        location: position.location,
        employment_type: position.type,
        status: position.status,
        is_public: position.is_public,
        salary_min: position.salary.min,
        salary_max: position.salary.max,
        salary_currency: position.salary.currency,
        application_deadline: position.deadline,
        priority: position.priority,
        requirements: position.requirements || [],
        skills_required: position.skills_required || [],
        certifications: position.certifications || []
      }

      const response = await apiClient.updatePosition(params.id, updateData)
      
      if (response.success) {
        setIsEditing(false)
        alert('Position updated successfully!')
        loadPositionDetail()
      } else {
        throw new Error(response.error || 'Update failed')
      }
    } catch (err) {
      alert('Failed to update position: ' + handleApiError(err))
    } finally {
      setSaving(false)
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft': return 'bg-gray-100 text-gray-800'
      case 'active': case 'open': return 'bg-green-100 text-green-800'
      case 'paused': return 'bg-yellow-100 text-yellow-800'
      case 'filled': case 'closed': return 'bg-blue-100 text-blue-800'
      case 'cancelled': return 'bg-red-100 text-red-800'
      case 'pending_review': return 'bg-yellow-50 text-yellow-800'
      case 'rejected': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'text-red-600'
      case 'medium': return 'text-yellow-600'
      case 'low': return 'text-green-600'
      default: return 'text-gray-600'
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <Loader className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-600" />
          <p className="text-gray-600">Loading position details...</p>
        </div>
      </div>
    )
  }

  if (error || !position) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Position</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button onClick={() => navigate(-1)} className="btn-primary">
            Go Back
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <div className="flex items-center">
        <button
          onClick={() => navigate(-1)}
          className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
      </div>

      {isReviewMode && (
        <div className="bg-white shadow rounded-lg border border-yellow-200 p-4 flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-700">This position is pending review.</p>
            <p className="text-xs text-gray-500">Status: {position.status}</p>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setShowRejectModal(true)}
              disabled={reviewActionLoading}
              className="px-4 py-2 rounded-md border border-red-500 text-red-600 hover:bg-red-50 disabled:opacity-50"
            >
              {reviewActionLoading ? 'Processing...' : 'Reject'}
            </button>
            <button
              onClick={handleApprove}
              disabled={reviewActionLoading}
              className="px-4 py-2 rounded-md bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
            >
              {reviewActionLoading ? 'Processing...' : 'Approve & Open'}
            </button>
          </div>
        </div>
      )}

      {showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-lg">
            <h3 className="text-lg font-semibold mb-2">Reject Position</h3>
            <p className="text-sm text-gray-600 mb-4">Provide a reason for rejection. This will be emailed to the position creator.</p>
            <textarea
              className="w-full border rounded-md p-3 text-sm mb-4"
              rows={4}
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Reason for rejection"
            />
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowRejectModal(false)}
                className="px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                disabled={reviewActionLoading}
                className="px-4 py-2 rounded-md bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
              >
                {reviewActionLoading ? 'Processing...' : 'Reject Position'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Collapsible Position Details */}
      <CollapsiblePositionDetails
        position={position}
        isEditing={isEditing}
        saving={saving}
        copied={copied}
        onEdit={() => setIsEditing(true)}
        onSave={handleSave}
        onCancel={() => setIsEditing(false)}
        onInputChange={handleInputChange}
        onSalaryChange={handleSalaryChange}
        onSkillsChange={handleSkillsChange}
        onCertificationsChange={handleCertificationsChange}
        copyPublicUrl={copyPublicUrl}
        getStatusColor={getStatusColor}
        getPriorityColor={getPriorityColor}
      />

      {/* Tabs for Candidates vs Applicants */}
      <div className="bg-white rounded-lg shadow">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 px-6">
            <button
              onClick={() => setActiveTab('candidates')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'candidates'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <Users className="w-4 h-4" />
                <span>Shortlisted</span>
                {loadingCounts ? (
                  <span className="bg-gray-100 text-gray-600 py-1 px-2 rounded-full text-xs">
                    <Loader className="w-3 h-3 animate-spin" />
                  </span>
                ) : (
                  <span className="bg-gray-100 text-gray-600 py-1 px-2 rounded-full text-xs">
                    {applicantCounts.shortlisted_count}
                  </span>
                )}
              </div>
            </button>
            <button
              onClick={() => setActiveTab('applicants')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'applicants'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <Globe className="w-4 h-4" />
                <span>Job Applicants</span>
                {loadingCounts ? (
                  <span className="bg-gray-100 text-gray-600 py-1 px-2 rounded-full text-xs">
                    <Loader className="w-3 h-3 animate-spin" />
                  </span>
                ) : (
                  <span className="bg-gray-100 text-gray-600 py-1 px-2 rounded-full text-xs">
                    {applicantCounts.job_applicants_count}
                  </span>
                )}
              </div>
            </button>
            <button
              onClick={() => setActiveTab('interviews')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'interviews'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <Calendar className="w-4 h-4" />
                <span>Interviews</span>
                {loadingCounts ? (
                  <span className="bg-gray-100 text-gray-600 py-1 px-2 rounded-full text-xs">
                    <Loader className="w-3 h-3 animate-spin" />
                  </span>
                ) : (
                  <span className="bg-gray-100 text-gray-600 py-1 px-2 rounded-full text-xs">
                    {applicantCounts.interviews_count}
                  </span>
                )}
              </div>
            </button>
            <button
              onClick={() => setActiveTab('onhold')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'onhold'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <Pause className="w-4 h-4" />
                <span>On Hold</span>
                {loadingCounts ? (
                  <span className="bg-gray-100 text-gray-600 py-1 px-2 rounded-full text-xs">
                    <Loader className="w-3 h-3 animate-spin" />
                  </span>
                ) : (
                  <span className="bg-gray-100 text-gray-600 py-1 px-2 rounded-full text-xs">
                    {applicantCounts.onhold_count}
                  </span>
                )}
              </div>
            </button>
            <button
              onClick={() => setActiveTab('rejected')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'rejected'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <X className="w-4 h-4" />
                <span>Rejected</span>
                {loadingCounts ? (
                  <span className="bg-gray-100 text-gray-600 py-1 px-2 rounded-full text-xs">
                    <Loader className="w-3 h-3 animate-spin" />
                  </span>
                ) : (
                  <span className="bg-gray-100 text-gray-600 py-1 px-2 rounded-full text-xs">
                    {applicantCounts.rejected_count}
                  </span>
                )}
              </div>
            </button>
            <button
              onClick={() => setActiveTab('shares')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'shares'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <Share2 className="w-4 h-4" />
                <span>Shares</span>
                <span className="bg-gray-100 text-gray-600 py-1 px-2 rounded-full text-xs">
                  {positionShares.length}
                </span>
              </div>
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'candidates' ? (
          <CandidatesTableView
            candidates={positionCandidates}
            onAddCandidate={() => setShowAssociateModal(true)}
            loading={loadingCandidates}
            positionId={position?.id || params.id}
            positionSkills={position?.skills_required || []}
            positionCertifications={position?.certifications || []}
            onCandidateUpdated={() => {
              loadPositionCandidates() // Refresh candidates list
              loadApplicantCounts() // Refresh counts
            }}
          />
        ) : activeTab === 'applicants' ? (
          <JobApplicantsView
            positionUuid={position?.id || params.id}
            positionSkills={position?.skills_required || []}
            positionCertifications={position?.certifications || []}
            onApplicantShortlisted={() => {
              loadApplicantCounts() // Refresh counts when someone is shortlisted
              loadPositionCandidates() // Also refresh candidates
            }}
          />
        ) : activeTab === 'interviews' ? (
          <PositionInterviewsView
            interviews={positionInterviews}
            loading={loadingInterviews}
            onRefresh={() => {
              loadPositionInterviews()
              loadApplicantCounts()
            }}
          />
        ) : activeTab === 'onhold' ? (
          <JobApplicantsView
            positionUuid={position?.id || params.id}
            positionSkills={position?.skills_required || []}
            positionCertifications={position?.certifications || []}
            statusFilter="on_hold"
            onApplicantShortlisted={() => {
              loadApplicantCounts() // Refresh counts
              loadPositionCandidates() // Also refresh candidates
            }}
          />
        ) : (
          activeTab === 'rejected' ? (
            <JobApplicantsView
              positionUuid={position?.id || params.id}
              positionSkills={position?.skills_required || []}
              positionCertifications={position?.certifications || []}
              statusFilter="rejected"
              onApplicantShortlisted={() => {
                loadApplicantCounts() // Refresh counts
                loadPositionCandidates() // Also refresh candidates
              }}
            />
          ) : (
            <div className="p-6">
              {loadingShares ? (
                <div className="flex items-center space-x-3 text-gray-600">
                  <Loader className="w-4 h-4 animate-spin" />
                  <span>Loading shares...</span>
                </div>
              ) : positionShares.length === 0 ? (
                <div className="text-gray-600">No shares found for this position.</div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">Team Shares</h4>
                    <div className="space-y-3">
                      {positionShares.filter((s: any) => s.share_type === 'team').map((share: any, idx: number) => (
                        <div key={`team-share-${idx}`} className="border rounded-lg p-4 flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-gray-900">{share.team_name || 'Team'}</p>
                            <p className="text-xs text-gray-500">Shared by {share.shared_by || 'Unknown'} on {share.created_at || share.createdAt}</p>
                            {share.notes && <p className="text-xs text-gray-500 mt-1">{share.notes}</p>}
                          </div>
                          {currentUserId && share.shared_by_uuid === currentUserId && (
                            <button
                              onClick={() => apiClient.removePositionShare(share.share_uuid || share.id)}
                              className="text-red-600 hover:text-red-800 text-xs"
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">Individual Shares</h4>
                    <div className="space-y-3">
                      {positionShares.filter((s: any) => s.share_type === 'individual').map((share: any, idx: number) => (
                        <div key={`user-share-${idx}`} className="border rounded-lg p-4 flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-gray-900">{share.shared_with?.name || share.shared_with?.email || 'User'}</p>
                            <p className="text-xs text-gray-500">Shared by {share.shared_by || 'Unknown'} on {share.created_at || share.createdAt}</p>
                            {share.notes && <p className="text-xs text-gray-500 mt-1">{share.notes}</p>}
                          </div>
                          {currentUserId && share.shared_by_uuid === currentUserId && (
                            <button
                              onClick={() => apiClient.removePositionShare(share.share_uuid || share.id)}
                              className="text-red-600 hover:text-red-800 text-xs"
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )
        )}
      </div>


      {/* Associate Candidate Modal */}
      <AssociateCandidateModal
        open={showAssociateModal}
        onClose={() => setShowAssociateModal(false)}
        position={position}
        onAssociated={() => {
          setShowAssociateModal(false)
          loadPositionCandidates() // Refresh candidates list
        }}
      />
    </div>
  )
}
